import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import IntermittentModal from "@/components/IntermittentModal";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Phone, Mail, Euro } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function IntermittentsPage() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [periods, setPeriods] = useState({ years: [] });
  const [year, setYear] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = year === "all" ? {} : { year: Number(year) };
      const [{ data: inter }, { data: stats }, { data: p }] = await Promise.all([
        api.get("/intermittents"),
        api.get("/stats", { params }),
        api.get("/periods"),
      ]);
      setItems(inter);
      setPeriods(p);
      const map = {};
      (stats.by_intermittent || []).forEach((x) => (map[x.name] = x.value));
      setCounts(map);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const shown = (year === "all" ? items : items.filter((i) => (counts[i.name] || 0) > 0))
    .slice()
    .sort((a, b) => (counts[b.name] || 0) - (counts[a.name] || 0));

  const doDelete = async () => {
    try {
      await api.delete(`/intermittents/${toDelete.id}`);
      toast.success("Régisseur supprimé");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const initials = (name) => name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Régisseurs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {shown.length} régisseur{shown.length > 1 ? "s" : ""} · {year === "all" ? "toutes années" : `année ${year}`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={String(year)} onValueChange={setYear}>
            <SelectTrigger className="w-[150px]" data-testid="inter-year-selector">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes années</SelectItem>
              {periods.years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }}
            className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold" data-testid="btn-add-intermittent">
            <Plus className="h-4 w-4 mr-1.5" /> Régisseur
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {shown.map((i) => (
          <div key={i.id} className="rounded-xl border border-border bg-card p-4 hover:border-primary/40 transition-colors group" data-testid={`inter-card-${i.id}`}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full flex items-center justify-center font-display font-bold text-black text-sm"
                  style={{ background: i.color }}>
                  {initials(i.name)}
                </div>
                <div>
                  <div className="font-semibold leading-tight">{i.name}</div>
                  <div className="text-xs text-muted-foreground">{i.metier || "—"}</div>
                </div>
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <button onClick={() => { setEditing(i); setModalOpen(true); }} className="text-muted-foreground hover:text-primary p-1" data-testid={`edit-inter-${i.id}`}>
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => setToDelete(i)} className="text-muted-foreground hover:text-destructive p-1" data-testid={`delete-inter-${i.id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="font-display text-3xl font-extrabold text-primary tabular-nums">{counts[i.name] || 0}</span>
              <span className="text-xs text-muted-foreground">concerts affectés</span>
            </div>
            <div className="mt-3 space-y-1 text-xs text-muted-foreground">
              {i.email && <div className="flex items-center gap-2 truncate"><Mail className="h-3 w-3" />{i.email}</div>}
              {i.phone && <div className="flex items-center gap-2"><Phone className="h-3 w-3" />{i.phone}</div>}
              {i.tarif && <div className="flex items-center gap-2"><Euro className="h-3 w-3" />{i.tarif}</div>}
            </div>
          </div>
        ))}
      </div>

      <IntermittentModal open={modalOpen} onOpenChange={setModalOpen} item={editing} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-inter-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce régisseur ?</AlertDialogTitle>
            <AlertDialogDescription>
              « {toDelete?.name} » sera retiré de tous les concerts auxquels il est affecté.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-inter">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
